import { type KeyboardEvent, type ReactNode, useRef } from 'react';

export interface TabItem<T extends string> {
  id: T;
  label: string;
  badge?: ReactNode;
}

export function Tabs<T extends string>({
  label,
  tabs,
  value,
  onChange,
}: {
  label: string;
  tabs: TabItem<NoInfer<T>>[];
  value: T;
  onChange(id: NoInfer<T>): void;
}) {
  const refs = useRef(new Map<T, HTMLButtonElement>());
  const go = (event: KeyboardEvent, index: number) => {
    event.preventDefault();
    const tab = tabs[(index + tabs.length) % tabs.length];
    if (!tab) return;
    onChange(tab.id);
    refs.current.get(tab.id)?.focus();
  };
  const current = tabs.findIndex((t) => t.id === value);
  return (
    <div role="tablist" aria-label={label} className="flex border-b border-line">
      {tabs.map((t) => {
        const selected = t.id === value;
        return (
          <button
            key={t.id}
            ref={(el) => {
              if (el) refs.current.set(t.id, el);
            }}
            type="button"
            role="tab"
            id={`tab-${t.id}`}
            aria-selected={selected}
            aria-controls={`panel-${t.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.id)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') go(e, current + 1);
              if (e.key === 'ArrowLeft') go(e, current - 1);
              if (e.key === 'Home') go(e, 0);
              if (e.key === 'End') go(e, tabs.length - 1);
            }}
            className={`flex min-h-11 flex-1 items-center justify-center gap-1.5 px-2 font-display text-xs font-bold uppercase tracking-[0.12em] ${
              selected
                ? 'border-b-2 border-accent bg-gradient-to-t from-accent/10 to-transparent text-accent'
                : 'text-faint hover:text-text'
            }`}
          >
            {t.label}
            {t.badge}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} className="pt-4">
      {children}
    </div>
  );
}
